// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
// import { 
//   ChevronLeft, Star, AlertTriangle, 
//   Lock, Info 
// } from "lucide-react";
// import api from "../../common/apiClient";
// import SummaryApi from "../../common/SummaryApi";
// import PageLoader from "../../helpers/PageLoader";
// import { useSelector } from "react-redux";

// export default function ReviewEvaluation() {
//   const { portalName, id } = useParams();
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(true);
//   const [submitting, setSubmitting] = useState(false);
//   const [skipping, setSkipping] = useState(false); // Track stage skip loading state (Added)
//   const [showConfirmModal, setShowConfirmModal] = useState(false);
//   const [reviewData, setReviewData] = useState(null);
//   const currentUser = useSelector((state) => state?.user?.user);

//   // Form states for active evaluation
//   const [ratings, setRatings] = useState({}); 
//   const [feedbacks, setFeedbacks] = useState({}); 
//   const [overallFeedback, setOverallFeedback] = useState("");
//   const [nextYearGoals, setNextYearGoals] = useState("");
//   const [additionalFeedback, setAdditionalFeedback] = useState("");

//   // Helper function to resolve dynamic KRA Template ID as clean string key
//   const getKraKey = (kra) => {
//     if (!kra) return "";
//     const idField = kra.kraId?._id || kra.kraId;
//     return idField ? idField.toString() : "";
//   };

//   const loadReviewDetails = useCallback(async () => {
//     setLoading(true);
//     try {
//       const [reviewRes] = await Promise.all([
//         api.get(SummaryApi.getReviewByIdKra?.url(id) || `/api/hr/kra/reviews/view-one/${id}`),
//       ]);

//       if (reviewRes.data.success) {
//         const data = reviewRes.data.data;
//         setReviewData(data);

//         const activeStage = data.stages[data.currentStageIndex];
//         const activeStageKey = activeStage?.stageKey;

//         const initialRatings = {};
//         const initialFeedbacks = {};

//         data.kras.forEach((k) => {
//           const key = getKraKey(k);
//           const existingEval = k.evaluations?.find(e => e.stageKey === activeStageKey);
//           initialRatings[key] = existingEval ? String(existingEval.rating) : "";
//           initialFeedbacks[key] = existingEval ? existingEval.feedback : "";
//         });

//         setRatings(initialRatings);
//         setFeedbacks(initialFeedbacks);

//         const existingOverall = data.overallEvaluations?.find(oe => oe.stageKey === activeStageKey);
//         if (existingOverall) {
//           setOverallFeedback(existingOverall.overallFeedback || "");
//           setNextYearGoals(existingOverall.nextYearGoalsFeedback || "");
//           setAdditionalFeedback(existingOverall.additionalFeedback || "");
//         }
//       }
//     } catch {
//       toast.error("Failed to load review sheet configurations.");
//     } finally {
//       setLoading(false);
//     }
//   }, [id]);

//   useEffect(() => {
//     loadReviewDetails();
//   }, [loadReviewDetails]);

//   // Evaluates whether it's the current logged-in user's active turn to submit reviews
//   const activeStageDetails = useMemo(() => {
//     if (!reviewData || !currentUser) return { isMyTurn: false, stage: null };
    
//     const stage = reviewData.stages[reviewData.currentStageIndex];
//     if (!stage) return { isMyTurn: false, stage: null };

//     const evaluatorIdStr = stage.evaluatorId?._id ? stage.evaluatorId._id.toString() : stage.evaluatorId?.toString();
//     const currentUserIdStr = currentUser._id?.toString();

//     const isCompletedReview = reviewData.status === "COMPLETED";
//     const isMyTurn = !isCompletedReview && evaluatorIdStr && currentUserIdStr && evaluatorIdStr === currentUserIdStr;
//     return { isMyTurn, stage };
//   }, [reviewData, currentUser]);

//   // ✅ Strictly checks if the active stage is a mandatory Self Appraisal stage (Added)
//   const isSelfStage = useMemo(() => {
//     if (!reviewData) return false;
//     const currentStage = reviewData.stages[reviewData.currentStageIndex];
//     if (!currentStage) return false;

//     const stageKey = String(currentStage.stageKey).toLowerCase();
//     const isSelfKey = stageKey.includes("self") || stageKey.includes("employee");

//     const evaluatorId = currentStage.evaluatorId?._id || currentStage.evaluatorId;
//     const userId = reviewData.userId?._id || reviewData.userId;
//     const isSelfEvaluator = evaluatorId && userId && evaluatorId.toString() === userId.toString();

//     return isSelfKey || isSelfEvaluator;
//   }, [reviewData]);

//   // Resolves the evaluator display name dynamically based on stage metadata
//   const getEvaluatorName = (stageKey, evaluatorId) => {
//     if (!reviewData?.stages) return "Evaluator";
    
//     const stageObj = reviewData.stages.find(s => s.stageKey === stageKey);
//     if (stageObj?.evaluatorId) {
//       const evalObj = stageObj.evaluatorId;
//       if (evalObj.firstName) {
//         return `${evalObj.firstName} ${evalObj.lastName || ""}`.trim();
//       }
//     }
    
//     if (evaluatorId && typeof evaluatorId === 'object' && evaluatorId.firstName) {
//       return `${evaluatorId.firstName} ${evaluatorId.lastName || ""}`.trim();
//     }
    
//     return "Evaluator";
//   };

//   const handleRatingSelect = (kraId, ratingValue) => {
//     setRatings(prev => ({ ...prev, [kraId]: ratingValue }));
//   };

//   const handleFeedbackChange = (kraId, text) => {
//     setFeedbacks(prev => ({ ...prev, [kraId]: text }));
//   };

//   // Live calculation of overall cumulative weighted score based on active ratings input
//   const liveOverallScore = useMemo(() => {
//     if (!reviewData || !reviewData.kras) return 0;
//     let scoreSum = 0;
//     reviewData.kras.forEach(k => {
//       const key = getKraKey(k);
//       const rating = parseFloat(ratings[key]) || 0;
//       scoreSum += rating * (Number(k.weightage) / 100);
//     });
//     return scoreSum;
//   }, [ratings, reviewData]);

//   // Form Validation checks
//   const handleValidateSubmission = () => {
//     const invalidRatings = reviewData.kras.filter(k => {
//       const key = getKraKey(k);
//       const numVal = parseFloat(ratings[key]);
//       return isNaN(numVal) || numVal < 1 || numVal > 5;
//     });

//     if (invalidRatings.length > 0) {
//       return toast.error("Please enter a valid rating between 1.0 and 5.0 for all Key Result Areas.");
//     }

//     const missingComments = reviewData.kras.filter(k => {
//       const key = getKraKey(k);
//       return !feedbacks[key]?.trim();
//     });
//     if (missingComments.length > 0) {
//       return toast.error("Feedback/Comments are mandatory for all KRAs.");
//     }

//     if (!overallFeedback.trim()) {
//       return toast.error("Please write overall feedback summarizing this review stage.");
//     }

//     setShowConfirmModal(true);
//   };

//   const handleSubmitEvaluation = async () => {
//     setShowConfirmModal(false);
//     setSubmitting(true);

//     const payload = {
//       reviewId: reviewData._id,
//       stageKey: activeStageDetails.stage?.stageKey,
//       kras: reviewData.kras.map(k => {
//         const key = getKraKey(k);
//         return {
//           kraId: k.kraId?._id || k.kraId,
//           rating: Number(parseFloat(ratings[key]).toFixed(2)),
//           feedback: feedbacks[key]
//         };
//       }),
//       overallFeedback: {
//         overallFeedback,
//         nextYearGoalsFeedback: nextYearGoals,
//         additionalFeedback,
//         attachments: []
//       }
//     };

//     try {
//       const url = SummaryApi.startKraReviewEvaluate?.url || "/api/hr/kra/reviews/evaluate";
//       const res = await api.post(url, payload);
//       if (res.data.success) {
//         toast.success("Your evaluation has been successfully submitted and locked!");
//         navigate(`/${portalName}/hr/performance-management`);
//       }
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Failed to submit evaluation scores.");
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   // ✅ Stage Skip/Bypass Handler integrated into workspace form (Added)
//   const handleSkipStage = async () => {
//     if (reviewData.status === "COMPLETED") return;
    
//     if (isSelfStage) {
//       return toast.error("The Self Assessment stage is mandatory and cannot be skipped.");
//     }

//     const currentStageName = activeStageDetails.stage?.stageName || "Active Stage";
//     if (!window.confirm(`Are you sure you want to bypass/skip the current evaluation stage (${currentStageName})?`)) {
//       return;
//     }

//     setSkipping(true);
//     try {
//       const url = SummaryApi.skipKraReviewStage?.url || "/api/hr/kra/reviews/skip";
//       const res = await api.post(url, { reviewId: reviewData._id });
//       if (res.data.success) {
//         toast.success("Active review stage bypassed successfully.");
//         navigate(`/${portalName}/hr/performance-management`);
//       }
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Failed to bypass/skip evaluation stage.");
//     } finally {
//       setSkipping(false);
//     }
//   };

//   if (loading) return <PageLoader />;
//   if (!reviewData) return <div className="text-center py-10 text-xs">Review details not found.</div>;

//   return (
//     <div className="p-2 space-y-6 animate-in fade-in duration-300">
//       {/* Navigation Header */}
//       <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
//         <button 
//           onClick={() => navigate(-1)} 
//           className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors border border-red-100"
//         >
//           <ChevronLeft className="w-5 h-5" />
//         </button>
//         <div className="text-left">
//           <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Appraisal Sheet - FY {reviewData.year}</span>
//           <h2 className="text-lg font-bold text-gray-900 leading-tight">{reviewData.period} Performance Review</h2>
//         </div>
//       </div>

//       {/* WORKFLOW PROGRESS STEPPER */}
//       <div className="bg-white border rounded-xl p-6 shadow-sm space-y-4">
//         <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block text-left">Appraisal Lifecycle Progress</span>
        
//         <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 relative">
//           {reviewData.stages.map((stage, idx) => {
//             const isCompletedReview = reviewData.status === "COMPLETED";
//             const isCompleted = isCompletedReview || idx < reviewData.currentStageIndex;
//             const isActive = !isCompletedReview && idx === reviewData.currentStageIndex;
//             const isFuture = !isCompletedReview && idx > reviewData.currentStageIndex;

//             return (
//               <div key={idx} className="flex-1 flex items-center gap-3 w-full relative">
//                 <div className={`
//                   w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-xs border-2 transition-all
//                   ${isCompleted ? "bg-green-500 border-green-500 text-white" : ""}
//                   ${isActive ? "bg-primary border-primary text-white ring-4 ring-red-100" : ""}
//                   ${isFuture ? "bg-gray-50 border-gray-200 text-gray-400" : ""}
//                 `}>
//                   {isCompleted ? "✓" : idx + 1}
//                 </div>
                
//                 <div className="min-w-0 text-left">
//                   <p className={`text-xs font-bold ${isActive ? "text-primary" : "text-gray-700"}`}>
//                     {stage.stageName}
//                   </p>
//                   <p className="text-[10px] text-gray-400 truncate">
//                     Evaluator: {stage.evaluatorId?.firstName ? `${stage.evaluatorId.firstName} ${stage.evaluatorId.lastName}` : "Pending Resolution"}
//                   </p>
//                 </div>

//                 {idx < reviewData.stages.length - 1 && (
//                   <div className="hidden lg:block absolute top-4 left-32 right-2 h-0.5 bg-gray-100" />
//                 )}
//               </div>
//             );
//           })}
//         </div>
//       </div>

//       {/* WORKSPACE DETAILED ANALYSIS */}
//       <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
//         <div className="xl:col-span-2 space-y-5">
//           {reviewData.kras.map((k, index) => {
//             const key = getKraKey(k);
//             const currentRating = parseFloat(ratings[key]) || 0;
//             const liveWeightedContribution = (currentRating * Number(k.weightage)) / 100;

//             return (
//               <div key={key} className="bg-white border hover:border-primary rounded-xl p-6 shadow-sm space-y-4 transition-all duration-300">
//                 <div className="flex items-center justify-between gap-4 border-b pb-3">
//                   <div className="flex items-center gap-2">
//                     <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 font-bold text-xs">
//                       {String(index + 1).padStart(2, "0")}
//                     </span>
//                     <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight">{k.name}</h3>
//                   </div>
//                   <span className="text-[10px] bg-red-100 text-red-600 font-bold px-2 py-0.5 rounded border border-red-200 uppercase">
//                     Weight: {k.weightage}%
//                   </span>
//                 </div>

//                 <div className="text-left space-y-3">
//                   <p className="text-xs text-gray-500 leading-relaxed">{k.description || "No description provided."}</p>

//                   {/* Dynamic Target Goals parameters rendering cleanly */}
//                   <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-2.5 border-y border-dashed border-gray-100 text-[11px] text-gray-500 bg-gray-50/50 px-3 rounded-lg font-medium">
//                     <div>
//                       <span className="text-[9px] text-gray-400 font-bold uppercase block">Review Frequency</span>
//                       <span className="font-bold text-gray-700">{k.reviewCycle?.replace("_", " ") || "N/A"}</span>
//                     </div>
//                     <div>
//                       <span className="text-[9px] text-gray-400 font-bold uppercase block">Monthly Target</span>
//                       <span className="font-bold text-gray-700">{k.monthlyTarget || "N/A"}</span>
//                     </div>
//                     <div>
//                       <span className="text-[9px] text-gray-400 font-bold uppercase block">Yearly Target</span>
//                       <span className="font-bold text-gray-700">{k.yearlyTarget || "N/A"}</span>
//                     </div>
//                   </div>
//                 </div>

//                 {/* HISTORY LOGS */}
//                 {k.evaluations?.length > 0 && (
//                   <div className="space-y-2 text-left">
//                     <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Evaluation History Log</p>
//                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
//                       {k.evaluations.map((evalItem, eIdx) => {
//                         const evaluatorName = getEvaluatorName(evalItem.stageKey, evalItem.evaluatorId);
//                         const weightedScore = (Number(evalItem.rating) * Number(k.weightage)) / 100;
                        
//                         return (
//                           <div key={eIdx} className="bg-slate-50 border border-slate-100 p-3.5 rounded-lg text-[11px] space-y-2 flex flex-col justify-between">
//                             <div>
//                               <div className="flex justify-between items-center mb-1">
//                                 <span className="font-bold text-slate-800">{evalItem.stageName}</span>
//                                 <span className="bg-slate-200 text-slate-700 font-bold px-1.5 py-0.2 rounded text-[10px]">{evalItem.rating} / 5</span>
//                               </div>
//                               <p className="text-slate-500 leading-relaxed italic">"{evalItem.feedback}"</p>
//                             </div>
                            
//                             <div className="pt-2 border-t border-dashed border-gray-200 text-[10px] text-slate-400">
//                               <span>Weighted Score: </span>
//                               <span className="font-bold text-slate-600">
//                                 {evalItem.rating} × {k.weightage}% = {weightedScore.toFixed(2)}
//                               </span>
//                             </div>

//                             <div className="flex justify-between items-center text-[9px] text-gray-400 border-t pt-1.5 mt-1">
//                               <span>By: <strong>{evaluatorName}</strong></span>
//                               {evalItem.submittedAt && (
//                                 <span>On: {new Date(evalItem.submittedAt).toLocaleDateString()}</span>
//                               )}
//                             </div>
//                           </div>
//                         );
//                       })}
//                     </div>
//                   </div>
//                 )}

//                 {/* PRECISE DECIMAL RATING INPUT & WEIGHTED SCORE CONTRIBUTION */}
//                 {activeStageDetails.isMyTurn ? (
//                   <div className="bg-gray-50/50 border border-dashed rounded-lg p-4 space-y-4 text-left">
//                     <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-3">
//                       <span className="text-xs font-bold text-gray-700">How do you rate the achievements for this KRA? *</span>
                      
//                       <div className="flex items-center gap-4">
//                         {/* Interactive Visual Star representation */}
//                         <div className="flex items-center gap-1 shrink-0">
//                           {[1, 2, 3, 4, 5].map((star) => {
//                             return (
//                               <button
//                                 key={star}
//                                 type="button"
//                                 onClick={() => handleRatingSelect(key, String(star))}
//                                 className="p-0.5 hover:scale-110 transition-transform cursor-pointer"
//                               >
//                                 <Star 
//                                   className={`w-5 h-5 ${
//                                     currentRating >= star 
//                                       ? "fill-amber-400 text-amber-400" 
//                                       : (currentRating > (star - 1)) 
//                                         ? "fill-amber-400/50 text-amber-400" 
//                                         : "text-gray-300"
//                                   }`} 
//                                 />
//                               </button>
//                             );
//                           })}
//                         </div>

//                         {/* DECIMAL INPUT BOX */}
//                         <div className="flex items-center gap-1.5 shrink-0">
//                           <input 
//                             type="text"
//                             value={ratings[key] || ""}
//                             onChange={(e) => {
//                               const val = e.target.value;
//                               if (val === "" || /^[1-5](\.[0-9]?)?$/.test(val)) {
//                                 handleRatingSelect(key, val);
//                               }
//                             }}
//                             onBlur={(e) => {
//                               const val = parseFloat(e.target.value);
//                               if (!isNaN(val)) {
//                                 const bounded = Math.min(5, Math.max(1, val));
//                                 handleRatingSelect(key, String(bounded));
//                               }
//                             }}
//                             placeholder="0.0"
//                             className="w-14 border rounded-md px-2 py-1 text-center text-xs font-bold outline-none focus:ring-1 focus:ring-red-200 h-8 bg-white shadow-sm"
//                           />
//                           <span className="text-xs text-gray-400 font-bold">/ 5.0</span>
//                         </div>
//                       </div>
//                     </div>

//                     <div className="space-y-1">
//                       <label className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider">Feedback & Achievements Justification *</label>
//                       <textarea
//                         value={feedbacks[key] || ""}
//                         onChange={(e) => handleFeedbackChange(key, e.target.value)}
//                         placeholder="Write dynamic achievement details, data metrics, or comments here..."
//                         rows={3}
//                         className="w-full text-xs p-3 border rounded-md outline-none focus:ring-1 focus:ring-red-200 bg-white"
//                       />
//                     </div>

//                     {/* Real-time Weighted Contribution Preview inside KRA Form */}
//                     <div className="flex justify-between items-center text-[10px] text-slate-500 font-bold bg-white p-2 border border-slate-100 rounded-lg">
//                       <span>Live Weighted Contribution Preview:</span>
//                       <span className="text-primary font-extrabold">
//                         {currentRating.toFixed(2)} × {k.weightage}% = {liveWeightedContribution.toFixed(2)} / 5.00
//                       </span>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg text-gray-400 text-xs font-medium border text-left">
//                     <Lock className="w-4 h-4 shrink-0" />
//                     <span>No active actions required for you in this stage.</span>
//                   </div>
//                 )}
//               </div>
//             );
//           })}
//         </div>

//         {/* RIGHT PANEL: OVERALL DETAILS & SUBMISSION */}
//         <div className="space-y-6">
//           <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-5 text-left">
//             <span className="font-bold text-gray-800 text-sm border-b pb-2 block">Stage Overall Summary</span>

//             {/* Live overall score dynamic banner added inside Sidebar summary */}
//             {activeStageDetails.isMyTurn && (
//               <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-center space-y-2">
//                 <span className="block text-[10px] text-primary font-bold uppercase tracking-wider">Live Weighted Score Preview</span>
//                 <p className="text-3xl font-extrabold text-primary">
//                   {liveOverallScore.toFixed(2)} <span className="text-sm font-normal text-gray-400">/ 5.00</span>
//                 </p>
//                 <span className="text-[10px] text-gray-400 font-semibold block leading-tight">
//                   Calculated dynamically from current inputs
//                 </span>
//               </div>
//             )}

//             {reviewData.overallEvaluations?.length > 0 && (
//               <div className="space-y-3.5 max-h-[350px] overflow-y-auto pr-1">
//                 {reviewData.overallEvaluations.map((oe, oeIdx) => {
//                   const evaluatorName = getEvaluatorName(oe.stageKey);
//                   return (
//                     <div key={oeIdx} className="bg-slate-50 border p-3.5 rounded-lg text-[11px] space-y-2.5">
//                       <div className="flex justify-between items-center font-bold">
//                         <span className="text-slate-800 uppercase text-[10px]">{oe.stageName || `${oe.stageKey} Stage`} Summary</span>
//                         <span className="bg-primary/10 text-primary font-bold px-2 py-0.5 rounded text-[10px]">Score: {oe.overallScore} / 5</span>
//                       </div>
//                       <div className="space-y-1 text-slate-600 leading-relaxed">
//                         <p><strong>Feedback:</strong> {oe.overallFeedback}</p>
//                         {oe.nextYearGoalsFeedback && <p><strong>Next Goals:</strong> {oe.nextYearGoalsFeedback}</p>}
//                         {oe.additionalFeedback && <p><strong>Additional Remarks:</strong> {oe.additionalFeedback}</p>}
//                       </div>
//                       <div className="flex justify-between items-center text-[9px] text-gray-400 border-t pt-1.5 mt-1">
//                         <span>Submitted By: <strong>{evaluatorName}</strong></span>
//                         {oe.submittedAt && (
//                           <span>Date: {new Date(oe.submittedAt).toLocaleString()}</span>
//                         )}
//                       </div>
//                     </div>
//                   );
//                 })}
//               </div>
//             )}

//             {activeStageDetails.isMyTurn ? (
//               <div className="space-y-4 pt-2 border-t">
//                 <div className="space-y-1">
//                   <label className="block text-xs font-bold text-gray-700">Overall Feedback Summarization *</label>
//                   <textarea
//                     value={overallFeedback}
//                     onChange={(e) => setOverallFeedback(e.target.value)}
//                     placeholder="Provide a comprehensive summary of your achievements and challenges..."
//                     rows={3}
//                     className="w-full text-xs p-3 border rounded-md outline-none focus:ring-1 focus:ring-red-200 bg-white shadow-sm"
//                   />
//                 </div>

//                 <div className="space-y-1">
//                   <label className="block text-xs font-bold text-gray-700">Future Growth / Next Cycle Goals</label>
//                   <textarea
//                     value={nextYearGoals}
//                     onChange={(e) => setNextYearGoals(e.target.value)}
//                     placeholder="Focus targets, learning roadmap, training requirements..."
//                     rows={2}
//                     className="w-full text-xs p-3 border rounded-md outline-none focus:ring-1 focus:ring-red-200 bg-white shadow-sm"
//                   />
//                 </div>

//                 <div className="space-y-1">
//                   <label className="block text-xs font-bold text-gray-700">Additional Remarks</label>
//                   <textarea
//                     value={additionalFeedback}
//                     onChange={(e) => setAdditionalFeedback(e.target.value)}
//                     placeholder="Enter support requests, tools requirements, or structural changes..."
//                     rows={2}
//                     className="w-full text-xs p-3 border rounded-md outline-none focus:ring-1 focus:ring-red-200 bg-white shadow-sm"
//                   />
//                 </div>

//                 <div className="flex gap-3 pt-2">
//                   <button
//                     type="button"
//                     onClick={handleValidateSubmission}
//                     className="flex-1 bg-primary hover:bg-secondary text-white font-bold py-3 rounded-lg text-xs uppercase hover:shadow-lg transition-all"
//                   >
//                     Submit Stage
//                   </button>

//                   {/* ✅ Added: Fast bypass stage button (Auto-hidden for mandatory SELF assessment stage) */}
//                   {!isSelfStage && (
//                     <button
//                       type="button"
//                       disabled={skipping}
//                       onClick={handleSkipStage}
//                       className="px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg text-xs uppercase transition-colors"
//                     >
//                       {skipping ? "Bypassing..." : "Skip"}
//                     </button>
//                   )}
//                 </div>
//               </div>
//             ) : (
//               <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl flex gap-3 text-xs text-amber-700">
//                 <Info className="w-5 h-5 shrink-0" />
//                 <p className="leading-relaxed">
//                   You are currently viewing this appraisal sheet in Read-Only Mode. Submissions are only permitted when the appraisal cycle points to your action sequence.
//                 </p>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* CONFIRM EVALUATION POP-UP MODAL */}
//       {showConfirmModal && (
//         <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
//           <div className="bg-white w-full max-w-md rounded-xl shadow-xl border overflow-hidden p-6 space-y-4 animate-in fade-in zoom-in-95 duration-200">
//             <div className="flex items-start gap-3">
//               <div className="p-3 bg-red-50 text-red-600 rounded-full shrink-0">
//                 <AlertTriangle className="w-6 h-6" />
//               </div>
//               <div className="text-left">
//                 <h3 className="font-bold text-gray-900 text-base">Lock & Submit Appraisal?</h3>
//                 <p className="text-xs text-gray-500 mt-1 leading-relaxed">
//                   Are you absolutely sure you want to finalize your ratings and feedbacks for this stage? Once locked, these values are immediately submitted to the next workflow stage and cannot be altered.
//                 </p>
//               </div>
//             </div>

//             <div className="flex items-center justify-end gap-3 pt-2">
//               <button 
//                 onClick={() => setShowConfirmModal(false)}
//                 className="px-4 py-2 text-xs font-bold rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors uppercase tracking-wider"
//               >
//                 No, Go Back
//               </button>
//               <button 
//                 onClick={handleSubmitEvaluation}
//                 disabled={submitting}
//                 className="px-4 py-2 text-xs font-bold rounded-lg bg-primary text-white hover:bg-secondary hover:shadow-md transition-all uppercase tracking-wider"
//               >
//                 {submitting ? "Locking..." : "Yes, Lock & Submit"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }


import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { 
  ChevronLeft, Star, AlertTriangle, 
  Lock, Info 
} from "lucide-react";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import PageLoader from "../../helpers/PageLoader";
import { useSelector } from "react-redux";

// Toggle configuration to allow overachievement score calculation above 100%
const ALLOW_OVERACHIEVEMENT_BONUS = true; 

// Helper to format system DB cycle keys to user-friendly labels
const formatCycleDisplay = (cycle) => {
  const mapping = {
    "1_WEEK": "Weekly",
    "2_WEEKS": "Bi-weekly",
    "1_MONTH": "Monthly",
    "2_MONTHS": "Bi-monthly",
    "3_MONTHS": "Quarterly",
    "6_MONTHS": "Half-Yearly",
    "12_MONTHS": "Annual"
  };
  return mapping[cycle] || cycle;
};

// Helper function to extract numeric values safely for live calculations
const parseNumericValue = (str) => {
  if (str === undefined || str === null) return null;
  const cleanStr = String(str).trim();
  if (!cleanStr) return null;
  const cleaned = cleanStr.replace(/[^0-9.]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
};

// Maps achievement percentage to the standard performance grades
const getAchievementRating = (percentage) => {
  if (percentage === null || percentage === undefined || isNaN(percentage)) return "N/A";
  if (percentage >= 110) return "Outstanding";
  if (percentage >= 100) return "Exceeds Expectations";
  if (percentage >= 90) return "Meets Expectations";
  if (percentage >= 75) return "Needs Improvement";
  return "Unsatisfactory";
};

// Returns Tailwind CSS badge classes for mapped performance grades
const getRatingBadgeClass = (rating) => {
  switch (rating) {
    case "Outstanding": return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "Exceeds Expectations": return "bg-teal-50 text-teal-700 border-teal-200";
    case "Meets Expectations": return "bg-blue-50 text-blue-700 border-blue-200";
    case "Needs Improvement": return "bg-amber-50 text-amber-700 border-amber-200";
    case "Unsatisfactory": return "bg-red-50 text-red-700 border-red-200";
    default: return "bg-gray-50 text-gray-700 border-gray-200";
  }
};

export default function ReviewEvaluation() {
  const { portalName, id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [skipping, setSkipping] = useState(false); 
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const currentUser = useSelector((state) => state?.user?.user);

  // Form states for active evaluation
  const [ratings, setRatings] = useState({}); 
  const [actualValues, setActualValues] = useState({}); 
  const [feedbacks, setFeedbacks] = useState({}); 
  const [overallFeedback, setOverallFeedback] = useState("");
  const [nextYearGoals, setNextYearGoals] = useState("");
  const [additionalFeedback, setAdditionalFeedback] = useState("");

  // Resolves nested KRA Template ID as a clean string key
  const getKraKey = (kra) => {
    if (!kra) return "";
    const idField = kra.kraId?._id || kra.kraId;
    return idField ? idField.toString() : "";
  };

  const loadReviewDetails = useCallback(async () => {
    setLoading(true);
    try {
      const [reviewRes] = await Promise.all([
        api.get(SummaryApi.getReviewByIdKra?.url(id) || `/api/hr/kra/reviews/view-one/${id}`),
      ]);

      if (reviewRes.data.success) {
        const data = reviewRes.data.data;
        setReviewData(data);

        const activeStage = data.stages[data.currentStageIndex];
        const activeStageKey = activeStage?.stageKey;

        const initialRatings = {};
        const initialActuals = {};
        const initialFeedbacks = {};

        data.kras.forEach((k) => {
          const key = getKraKey(k);
          const existingEval = k.evaluations?.find(e => e.stageKey === activeStageKey);
          
          initialRatings[key] = existingEval ? String(existingEval.rating) : "";
          initialActuals[key] = k.actualValue || ""; 
          initialFeedbacks[key] = existingEval ? existingEval.feedback : "";
        });

        setRatings(initialRatings);
        setActualValues(initialActuals);
        setFeedbacks(initialFeedbacks);

        const existingOverall = data.overallEvaluations?.find(oe => oe.stageKey === activeStageKey);
        if (existingOverall) {
          setOverallFeedback(existingOverall.overallFeedback || "");
          setNextYearGoals(existingOverall.nextYearGoalsFeedback || "");
          setAdditionalFeedback(existingOverall.additionalFeedback || "");
        }
      }
    } catch {
      toast.error("Failed to load review sheet configurations.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadReviewDetails();
  }, [loadReviewDetails]);

  // Evaluates whether it's the current logged-in user's active turn to submit reviews
  const activeStageDetails = useMemo(() => {
    if (!reviewData || !currentUser) return { isMyTurn: false, stage: null };
    
    const stage = reviewData.stages[reviewData.currentStageIndex];
    if (!stage) return { isMyTurn: false, stage: null };

    const evaluatorIdStr = stage.evaluatorId?._id ? stage.evaluatorId._id.toString() : stage.evaluatorId?.toString();
    const currentUserIdStr = currentUser._id?.toString();

    const isCompletedReview = reviewData.status === "COMPLETED";
    const isMyTurn = !isCompletedReview && evaluatorIdStr && currentUserIdStr && evaluatorIdStr === currentUserIdStr;
    return { isMyTurn, stage };
  }, [reviewData, currentUser]);

  // Checks if the current workflow stage is Self Assessment (Employee stage)
  const isSelfStage = useMemo(() => {
    if (!reviewData) return false;
    const currentStage = reviewData.stages[reviewData.currentStageIndex];
    if (!currentStage) return false;

    const stageKey = String(currentStage.stageKey).toLowerCase();
    const isSelfKey = stageKey.includes("self") || stageKey.includes("employee");

    const evaluatorId = currentStage.evaluatorId?._id || currentStage.evaluatorId;
    const userId = reviewData.userId?._id || reviewData.userId;
    const isSelfEvaluator = evaluatorId && userId && evaluatorId.toString() === userId.toString();

    return isSelfKey || isSelfEvaluator;
  }, [reviewData]);

  // Extracts employee's submitted self-appraisal details for managers reference
  const selfAppraisalSummary = useMemo(() => {
    if (!reviewData) return null;
    return reviewData.overallEvaluations?.find(oe => oe.stageKey === "self_assessment") || null;
  }, [reviewData]);

  const getEvaluatorName = (stageKey, evaluatorId) => {
    if (!reviewData?.stages) return "Evaluator";
    
    const stageObj = reviewData.stages.find(s => s.stageKey === stageKey);
    if (stageObj?.evaluatorId) {
      const evalObj = stageObj.evaluatorId;
      if (evalObj.firstName) {
        return `${evalObj.firstName} ${evalObj.lastName || ""}`.trim();
      }
    }
    
    if (evaluatorId && typeof evaluatorId === 'object' && evaluatorId.firstName) {
      return `${evaluatorId.firstName} ${evaluatorId.lastName || ""}`.trim();
    }
    
    return "Evaluator";
  };

  const handleRatingSelect = (kraId, ratingValue) => {
    setRatings(prev => ({ ...prev, [kraId]: ratingValue }));
  };

  const handleActualValueChange = (kraId, val) => {
    setActualValues(prev => ({ ...prev, [kraId]: val }));
  };

  const handleFeedbackChange = (kraId, text) => {
    setFeedbacks(prev => ({ ...prev, [kraId]: text }));
  };

  const totalActiveWeightage = useMemo(() => {
    if (!reviewData || !reviewData.kras) return 0;
    return reviewData.kras.reduce((sum, k) => sum + Number(k.weightage || 0), 0);
  }, [reviewData]);

  // Live calculation of overall cumulative weighted score (Backward Compatible with old records)
  // ✅ Upgraded to strictly support dynamic LOWER_IS_BETTER / HIGHER_IS_BETTER metricTypes in live calculation
  const liveOverallScore = useMemo(() => {
    if (!reviewData || !reviewData.kras) return 0;
    let scoreSum = 0;
    
    reviewData.kras.forEach(k => {
      const key = getKraKey(k);
      const actualVal = actualValues[key] || "";
      const actualNum = parseNumericValue(actualVal);

      const targetStr = k.targetValue || (k.reviewCycle === '12_MONTHS' ? k.yearlyTarget : k.monthlyTarget);
      const targetNum = k.targetValueNumeric || parseNumericValue(targetStr);

      if (targetNum && targetNum > 0 && actualNum !== null) {
        let achPct = 0;
        if (k.metricType === "LOWER_IS_BETTER") {
          achPct = actualNum === 0 ? 100 : (targetNum / actualNum) * 100;
        } else {
          achPct = (actualNum / targetNum) * 100;
        }

        const rawScore = (achPct / 100) * Number(k.weightage);
        
        scoreSum += ALLOW_OVERACHIEVEMENT_BONUS 
          ? rawScore 
          : Math.min(Number(k.weightage), rawScore);
      } else {
        const rating = parseFloat(ratings[key]) || 0;
        scoreSum += (rating / 5) * Number(k.weightage);
      }
    });
    return scoreSum;
  }, [actualValues, ratings, reviewData]);

  // Form Validation checks
  const handleValidateSubmission = () => {
    const missingInputs = reviewData.kras.filter(k => {
      const key = getKraKey(k);
      const targetStr = k.targetValue || (k.reviewCycle === '12_MONTHS' ? k.yearlyTarget : k.monthlyTarget);
      const targetNum = k.targetValueNumeric || parseNumericValue(targetStr);

      if (targetNum && targetNum > 0) {
        return !actualValues[key]?.trim(); // Require actual value if numeric target is configured
      } else {
        const numVal = parseFloat(ratings[key]);
        return isNaN(numVal) || numVal < 1 || numVal > 5; // Require rating otherwise
      }
    });

    if (missingInputs.length > 0) {
      return toast.error("Please enter a valid rating (1-5) or Actual Achievement for all Key Result Areas.");
    }

    const missingComments = reviewData.kras.filter(k => {
      const key = getKraKey(k);
      return !feedbacks[key]?.trim();
    });
    if (missingComments.length > 0) {
      return toast.error("Feedback/Comments are mandatory for all KRAs.");
    }

    if (!overallFeedback.trim()) {
      return toast.error("Please write overall feedback summarizing this review stage.");
    }

    setShowConfirmModal(true);
  };

  const handleSubmitEvaluation = async () => {
    setShowConfirmModal(false);
    setSubmitting(true);

    const payload = {
      reviewId: reviewData._id,
      stageKey: activeStageDetails.stage?.stageKey,
      kras: reviewData.kras.map(k => {
        const key = getKraKey(k);
        return {
          kraId: k.kraId?._id || k.kraId,
          rating: Number(parseFloat(ratings[key] || 0).toFixed(2)),
          feedback: feedbacks[key],
          actualValue: actualValues[key] || "" 
        };
      }),
      overallFeedback: {
        overallFeedback,
        nextYearGoalsFeedback: nextYearGoals,
        additionalFeedback,
        attachments: []
      }
    };

    try {
      const url = SummaryApi.startKraReviewEvaluate?.url || "/api/hr/kra/reviews/evaluate";
      const res = await api.post(url, payload);
      if (res.data.success) {
        toast.success("Your evaluation has been successfully submitted and locked!");
        navigate(`/${portalName}/hr/performance-management`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit evaluation scores.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkipStage = async () => {
    if (reviewData.status === "COMPLETED") return;
    
    if (isSelfStage) {
      return toast.error("The Self Assessment stage is mandatory and cannot be skipped.");
    }

    const currentStageName = activeStageDetails.stage?.stageName || "Active Stage";
    if (!window.confirm(`Are you sure you want to bypass/skip the current evaluation stage (${currentStageName})?`)) {
      return;
    }

    setSkipping(true);
    try {
      const url = SummaryApi.skipKraReviewStage?.url || "/api/hr/kra/reviews/skip";
      const res = await api.post(url, { reviewId: reviewData._id });
      if (res.data.success) {
        toast.success("Active review stage bypassed successfully.");
        navigate(`/${portalName}/hr/performance-management`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to bypass/skip evaluation stage.");
    } finally {
      setSkipping(false);
    }
  };

  if (loading) return <PageLoader />;
  if (!reviewData) return <div className="text-center py-10 text-xs">Review details not found.</div>;

  return (
    <div className="p-2 space-y-6 animate-in fade-in duration-300">
      {/* Navigation Header */}
      <div className="flex items-center gap-4 bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
        <button 
          onClick={() => navigate(-1)} 
          className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors border border-red-100"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-left">
          <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Appraisal Sheet - FY {reviewData.year}</span>
          <h2 className="text-lg font-bold text-gray-900 leading-tight">{reviewData.period} Performance Review</h2>
        </div>
      </div>

      {/* WORKFLOW PROGRESS STEPPER */}
      <div className="bg-white border rounded-xl p-3 shadow-sm space-y-4">
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block text-left">Appraisal Lifecycle Progress</span>
        
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 relative">
          {reviewData.stages.map((stage, idx) => {
            const isCompletedReview = reviewData.status === "COMPLETED";
            const isCompleted = isCompletedReview || idx < reviewData.currentStageIndex;
            const isActive = !isCompletedReview && idx === reviewData.currentStageIndex;
            const isFuture = !isCompletedReview && idx > reviewData.currentStageIndex;

            return (
              <div key={idx} className="flex-1 flex items-center gap-3 w-full relative">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-xs border-2 transition-all
                  ${isCompleted ? "bg-green-500 border-green-500 text-white" : ""}
                  ${isActive ? "bg-primary border-primary text-white ring-4 ring-red-100" : ""}
                  ${isFuture ? "bg-gray-50 border-gray-200 text-gray-400" : ""}
                `}>
                  {isCompleted ? "✓" : idx + 1}
                </div>
                
                <div className="min-w-0 text-left">
                  <p className={`text-xs font-bold ${isActive ? "text-primary" : "text-gray-700"}`}>
                    {stage.stageName}
                  </p>
                  <p className="text-[10px] text-gray-400 truncate">
                    Evaluator: {stage.evaluatorId?.firstName ? `${stage.evaluatorId.firstName} ${stage.evaluatorId.lastName}` : "Pending Resolution"}
                  </p>
                </div>

                {idx < reviewData.stages.length - 1 && (
                  <div className="hidden lg:block absolute top-4 left-32 right-2 h-0.5 bg-gray-100" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* WORKSPACE DETAILED ANALYSIS */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        <div className="xl:col-span-2 space-y-5">
          
          {/* Prominent Employee Self Appraisal Summary Box (Visible strictly during manager review stages) */}
          {selfAppraisalSummary && !isSelfStage && (
            <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-5 shadow-sm text-left flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-in fade-in duration-300">
              <div className="space-y-1 min-w-0 flex-1">
                <span className="text-[9px] bg-emerald-100 text-emerald-800 border border-emerald-200 font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                  Employee Self-Appraisal Reference Summary
                </span>
                <p className="text-xs font-bold text-gray-800 mt-2">
                  Overall Feedback Comment:{" "}
                  <span className="text-gray-500 italic font-medium">
                    "{selfAppraisalSummary.overallFeedback || "No feedback provided."}"
                  </span>
                </p>
              </div>
              <div className="shrink-0 bg-white border border-emerald-200 rounded-lg p-2.5 text-center shadow-sm">
                <span className="text-[9px] text-gray-400 font-bold block uppercase tracking-wider">Self Score</span>
                <span className="text-xl font-extrabold text-emerald-700">
                  {selfAppraisalSummary.overallScore?.toFixed(2)} / {totalActiveWeightage.toFixed(0)}
                </span>
              </div>
            </div>
          )}

          {reviewData.kras.map((k, index) => {
            const key = getKraKey(k);
            const currentRating = parseFloat(ratings[key]) || 0;
            const actualValueStr = actualValues[key] || "";
            const actualNum = parseNumericValue(actualValueStr);

            const targetStr = k.targetValue || (k.reviewCycle === '12_MONTHS' ? k.yearlyTarget : k.monthlyTarget);
            const targetNum = k.targetValueNumeric || parseNumericValue(targetStr);

            let liveAchievementPct = 0;
            let liveScoreContribution = 0;

            if (targetNum && targetNum > 0 && actualNum !== null) {
              // ✅ Added dynamic calculations for both LOWER_IS_BETTER and HIGHER_IS_BETTER in single item live preview
              if (k.metricType === "LOWER_IS_BETTER") {
                liveAchievementPct = actualNum === 0 ? 100 : (targetNum / actualNum) * 100;
              } else {
                liveAchievementPct = (actualNum / targetNum) * 100;
              }

              const rawScore = (liveAchievementPct / 100) * k.weightage;
              
              liveScoreContribution = ALLOW_OVERACHIEVEMENT_BONUS 
                ? rawScore 
                : Math.min(k.weightage, rawScore);
            } else {
              liveScoreContribution = (currentRating / 5) * k.weightage;
            }

            return (
              <div key={key} className="bg-white border hover:border-primary rounded-xl p-3 shadow-sm space-y-4 transition-all duration-300">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b pb-3 text-left">
                  {/* KRA Name & Number */}
                  <div className="flex items-start gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 font-bold text-xs mt-0.5">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight">{k.name}</h3>
                    </div>
                  </div>

                  {/* Weight and Earned Score Badges */}
                  <div className="flex sm:flex-col flex-wrap sm:items-end items-center gap-1.5 shrink-0 mt-2 sm:mt-0">
                    <span className="text-[10px] bg-red-100 text-red-600 font-bold px-2 py-0.5 rounded border border-red-200 uppercase">
                      Weight: {k.weightage}%
                    </span>
                    <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 font-extrabold px-2 py-0.5 rounded block">
                      Earned: {liveScoreContribution.toFixed(2)} / {k.weightage}.00
                    </span>
                  </div>
                </div>

                <div className="text-left space-y-3">
                  <p className="text-xs text-gray-500 leading-relaxed">{k.description || "No description provided."}</p>

                  {/* Simplified target layout showing only unified Target and Metric direction */}
                  {/* ✅ Upgraded to sm:grid-cols-3 layout to cleanly render Goal Direction parameters */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-2 text-[11px] text-gray-500 bg-gray-50/50 px-3 rounded-lg font-medium">
                    <div>
                      <span className="text-[9px] text-gray-400 font-bold uppercase block">Review Frequency</span>
                      <span className="font-bold text-gray-700">{formatCycleDisplay(k.reviewCycle)}</span>
                    </div>
                    <div>
                      <span className="text-[9px] text-primary font-bold uppercase block">Target</span>
                      <span className="font-extrabold text-gray-900">
                        {k.targetValue || k.monthlyTarget || k.yearlyTarget || "N/A"}
                      </span>
                    </div>
                    {/* ✅ Added Goal Direction Display */}
                    <div>
                      <span className="text-[9px] text-gray-400 font-bold uppercase block">Goal Direction</span>
                      <span className="font-semibold text-gray-700">
                        {k.metricType === "LOWER_IS_BETTER" ? "Lower is Better (Inverse)" : "Higher is Better (Direct)"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* HISTORY LOGS */}
                {k.evaluations?.length > 0 && (
                  <div className="space-y-2 text-left">
                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Evaluation History Log</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {k.evaluations.map((evalItem, eIdx) => {
                        const weightedContribution = (Number(evalItem.rating) / 5) * Number(k.weightage);
                        const evaluatorName = getEvaluatorName(evalItem.stageKey, evalItem.evaluatorId);
                        
                        return (
                          <div key={eIdx} className="bg-slate-50 border border-slate-100 p-3.5 rounded-lg text-[11px] space-y-2 flex flex-col justify-between">
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <span className="font-bold text-slate-800">{evalItem.stageName}</span>
                                <span className="bg-slate-200 text-slate-700 font-bold px-1.5 py-0.2 rounded text-[10px]">{evalItem.rating} / 5</span>
                              </div>
                              <p className="text-slate-500 leading-relaxed italic">"{evalItem.feedback}"</p>
                            </div>
                            
                            <div className="pt-2 border-t border-dashed border-gray-200 text-[10px] text-slate-400">
                              <span>Weighted Score: </span>
                              <span className="font-bold text-slate-600">
                                ({evalItem.rating} / 5) × {k.weightage}% = {weightedContribution.toFixed(2)}
                              </span>
                            </div>

                            <div className="flex justify-between items-center text-[9px] text-gray-400 border-t pt-1.5 mt-1">
                              <span>By: <strong>{evaluatorName}</strong></span>
                              {evalItem.submittedAt && (
                                <span>On: {new Date(evalItem.submittedAt).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* TARGET VS ACTUAL ACHIEVEMENT & RATING ACTION CONTAINER */}
                <div className="bg-gray-50/50 border border-dashed rounded-lg p-4 space-y-4 text-left">
                  
                  {/* 1. ACTUAL VALUE DISPLAY / INPUT */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-2">
                    <div className="text-left">
                      <label className="text-xs font-bold text-gray-700 block">Actual Achievement Value</label>
                      <span className="text-[10px] text-gray-400 font-medium">
                        {activeStageDetails.isMyTurn && isSelfStage 
                          ? "Specify what you completed (e.g., 4 or 99%) *" 
                          : "Reported during Self Assessment stage"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {activeStageDetails.isMyTurn && isSelfStage ? (
                        <input
                          type="text"
                          value={actualValueStr}
                          onChange={(e) => handleActualValueChange(key, e.target.value)}
                          placeholder="e.g., 4 or 99%"
                          className="w-32 border rounded-md px-3 py-1.5 text-xs font-bold bg-white text-center shadow-sm outline-none focus:ring-1 focus:ring-red-200"
                        />
                      ) : (
                        <span className="font-extrabold text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded border border-emerald-100 text-xs shrink-0">
                          {actualValueStr || "No value entered"}
                        </span>
                      )}
                      {liveAchievementPct > 0 && (
                        <div className="flex items-center gap-1.5">
                          {/* Achievement % Badge */}
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 font-extrabold px-2 py-1 rounded border border-emerald-200">
                            {liveAchievementPct.toFixed(2)}% Done
                          </span>
                          {/* Grade/Rating System Badge */}
                          <span className={`text-[10px] font-bold px-2 py-1 rounded border uppercase tracking-wider ${getRatingBadgeClass(getAchievementRating(liveAchievementPct))}`}>
                            {getAchievementRating(liveAchievementPct)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 2. RATING & STARS DISPLAY / INPUT */}
                  {(activeStageDetails.isMyTurn || (ratings[key] && ratings[key] !== "0")) ? (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-3">
                      <span className="text-xs font-bold text-gray-700">Self / Evaluator Rating *</span>
                      
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1 shrink-0">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => activeStageDetails.isMyTurn && handleRatingSelect(key, String(star))}
                              disabled={!activeStageDetails.isMyTurn}
                              className={`p-0.5 transition-transform ${activeStageDetails.isMyTurn ? "hover:scale-110 cursor-pointer" : "cursor-default"}`}
                            >
                              <Star 
                                className={`w-5 h-5 ${
                                  currentRating >= star 
                                    ? "fill-amber-400 text-amber-400" 
                                    : (currentRating > (star - 1)) 
                                      ? "fill-amber-400/50 text-amber-400" 
                                      : "text-gray-300"
                                }`} 
                              />
                            </button>
                          ))}
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          <input 
                            type="text"
                            value={ratings[key] || ""}
                            readOnly={!activeStageDetails.isMyTurn}
                            disabled={!activeStageDetails.isMyTurn}
                            onChange={(e) => {
                              if (!activeStageDetails.isMyTurn) return;
                              const val = e.target.value;
                              if (val === "" || /^[1-5](\.[0-9]?)?$/.test(val)) {
                                handleRatingSelect(key, val);
                              }
                            }}
                            onBlur={(e) => {
                              if (!activeStageDetails.isMyTurn) return;
                              const val = parseFloat(e.target.value);
                              if (!isNaN(val)) {
                                const bounded = Math.min(5, Math.max(1, val));
                                handleRatingSelect(key, String(bounded));
                              }
                            }}
                            placeholder="0.0"
                            className={`w-14 border rounded-md px-2 py-1 text-center text-xs font-bold outline-none h-8 shadow-sm ${
                              activeStageDetails.isMyTurn ? "focus:ring-1 focus:ring-red-200 bg-white" : "bg-gray-100 text-gray-500 cursor-not-allowed"
                            }`}
                          />
                          <span className="text-xs text-gray-400 font-bold">/ 5.0</span>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* 3. FEEDBACK TEXT AREA */}
                  {(activeStageDetails.isMyTurn || feedbacks[key]) ? (
                    <div className="space-y-1">
                      <label className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider">Feedback & Achievements Justification *</label>
                      {activeStageDetails.isMyTurn ? (
                        <textarea
                          value={feedbacks[key] || ""}
                          onChange={(e) => handleFeedbackChange(key, e.target.value)}
                          placeholder="Write dynamic achievement details, data metrics, or comments here..."
                          rows={3}
                          className="w-full text-xs p-3 border rounded-md outline-none focus:ring-1 focus:ring-red-200 bg-white"
                        />
                      ) : (
                        <p className="w-full text-xs p-3 border rounded-md bg-gray-50 text-gray-700 italic">
                          "{feedbacks[key] || "No feedback comments logged for this stage."}"
                        </p>
                      )}
                    </div>
                  ) : null}

                  {/* Final score contribution display */}
                  {(activeStageDetails.isMyTurn || currentRating > 0 || liveAchievementPct > 0) && (
                    <div className="flex justify-between items-center text-[10px] text-slate-500 font-bold bg-white p-2 border border-slate-100 rounded-lg">
                      <span>{activeStageDetails.isMyTurn ? "Live Weighted Score Contribution:" : "Final Weighted Score Contribution:"}</span>
                      <span className="text-primary font-extrabold">
                        {liveScoreContribution.toFixed(2)} / {k.weightage}.00
                      </span>
                    </div>
                  )}

                  {/* Lock status if not my turn */}
                  {!activeStageDetails.isMyTurn && (
                    <div className="flex items-center gap-2 bg-slate-50 p-2.5 rounded-lg text-gray-400 text-[10px] font-medium border border-gray-100">
                      <Lock className="w-3.5 h-3.5 shrink-0" />
                      <span>No active actions required for you in this stage (View Only Mode).</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* RIGHT PANEL: OVERALL DETAILS & SUBMISSION */}
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm space-y-5 text-left">
            <span className="font-bold text-gray-800 text-sm border-b pb-2 block">Stage Overall Summary</span>

            {/* Live overall score dynamic tracker */}
            {activeStageDetails.isMyTurn && (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-center space-y-2">
                <span className="block text-[10px] text-primary font-bold uppercase tracking-wider">Live Total Score Preview</span>
                <p className="text-3xl font-extrabold text-primary">
                  {liveOverallScore.toFixed(2)} <span className="text-sm font-normal text-gray-400">/ {totalActiveWeightage.toFixed(2)}</span>
                </p>
                <span className="text-[10px] text-gray-400 font-semibold block leading-tight">
                  Evaluated out of currently loaded active weightages
                </span>
              </div>
            )}

            {/* Structured Stage reflections matrix */}
            {reviewData.overallEvaluations?.length > 0 && (
              <div className="space-y-3.5 max-h-[450px] overflow-y-auto pr-1">
                {reviewData.overallEvaluations.map((oe, oeIdx) => {
                  const evaluatorName = getEvaluatorName(oe.stageKey);
                  const isOutOfFive = oe.overallScore <= 5;
                  
                  return (
                    <div key={oeIdx} className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs space-y-3 shadow-sm">
                      <div className="flex justify-between items-center font-bold border-b pb-1.5">
                        <span className="text-slate-800 uppercase text-[10px]">{oe.stageName || `${oe.stageKey} Stage`} Summary</span>
                        <span className="bg-primary/10 text-primary font-bold px-2.5 py-0.5 rounded text-[10px] border border-primary/20">
                          Score: {oe.overallScore?.toFixed(2)} {isOutOfFive ? "/ 5" : `/ ${totalActiveWeightage.toFixed(0)}`}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        <div>
                          <span className="text-[9px] text-slate-400 font-bold uppercase block">Overall Reflection feedback</span>
                          <p className="text-slate-700 italic font-medium leading-relaxed mt-0.5">"{oe.overallFeedback || "No feedback comments logged."}"</p>
                        </div>
                        {oe.nextYearGoalsFeedback && (
                          <div>
                            <span className="text-[9px] text-slate-400 font-bold uppercase block">Next Cycle learning focus / goals</span>
                            <p className="text-slate-700 italic font-medium leading-relaxed mt-0.5">"{oe.nextYearGoalsFeedback}"</p>
                          </div>
                        )}
                        {oe.additionalFeedback && (
                          <div>
                            <span className="text-[9px] text-slate-400 font-bold uppercase block">Remarks / Support requests</span>
                            <p className="text-slate-700 italic font-medium leading-relaxed mt-0.5">"{oe.additionalFeedback}"</p>
                          </div>
                        )}
                      </div>

                      <div className="flex justify-between items-center text-[9px] text-gray-400 border-t pt-1.5 mt-1 font-semibold">
                        <span>Submitted By: <strong>{evaluatorName}</strong></span>
                        {oe.submittedAt && (
                          <span>Date: {new Date(oe.submittedAt).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeStageDetails.isMyTurn ? (
              <div className="space-y-4 pt-2 border-t">
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-gray-700">Overall Feedback Summarization *</label>
                  <textarea
                    value={overallFeedback}
                    onChange={(e) => setOverallFeedback(e.target.value)}
                    placeholder="Provide a comprehensive summary of your achievements and challenges..."
                    rows={3}
                    className="w-full text-xs p-3 border rounded-md outline-none focus:ring-1 focus:ring-red-200 bg-white shadow-sm"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-bold text-gray-700">Future Growth / Next Cycle Goals</label>
                  <textarea
                    value={nextYearGoals}
                    onChange={(e) => setNextYearGoals(e.target.value)}
                    placeholder="Focus targets, learning roadmap, training requirements..."
                    rows={2}
                    className="w-full text-xs p-3 border rounded-md outline-none focus:ring-1 focus:ring-red-200 bg-white shadow-sm"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-bold text-gray-700">Additional Remarks</label>
                  <textarea
                    value={additionalFeedback}
                    onChange={(e) => setAdditionalFeedback(e.target.value)}
                    placeholder="Enter support requests, tools requirements, or structural changes..."
                    rows={2}
                    className="w-full text-xs p-3 border rounded-md outline-none focus:ring-1 focus:ring-red-200 bg-white shadow-sm"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleValidateSubmission}
                    className="flex-1 bg-primary hover:bg-secondary text-white font-bold py-3 rounded-lg text-xs uppercase hover:shadow-lg transition-all"
                  >
                    Submit Stage
                  </button>

                  {!isSelfStage && (
                    <button
                      type="button"
                      disabled={skipping}
                      onClick={handleSkipStage}
                      className="px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg text-xs uppercase transition-colors"
                    >
                      {skipping ? "Bypassing..." : "Skip"}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl flex gap-3 text-xs text-amber-700">
                <Info className="w-5 h-5 shrink-0" />
                <p className="leading-relaxed">
                  You are currently viewing this appraisal sheet in Read-Only Mode. Submissions are only permitted when the appraisal cycle points to your action sequence.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CONFIRM EVALUATION POP-UP MODAL */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-3 bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl border overflow-hidden p-3 space-y-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-start gap-3">
              <div className="p-3 bg-red-50 text-red-600 rounded-full shrink-0">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-gray-900 text-base">Lock & Submit Appraisal?</h3>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Are you absolutely sure you want to finalize your ratings and feedbacks for this stage? Once locked, these values are immediately submitted to the next workflow stage and cannot be altered.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button 
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-xs font-bold rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors uppercase tracking-wider"
              >
                No, Go Back
              </button>
              <button 
                onClick={handleSubmitEvaluation}
                disabled={submitting}
                className="px-4 py-2 text-xs font-bold rounded-lg bg-primary text-white hover:bg-secondary hover:shadow-md transition-all uppercase tracking-wider"
              >
                {submitting ? "Locking..." : "Yes, Lock & Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}